// react
import { SyntheticEvent, useState } from 'react';

// types
import { Account, Payment, Transaction } from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// mui
import { Box, Grid, Paper } from '@mui/material';
import { TabContext, TabPanel } from '@mui/lab';

// ui
import { Amount } from '../../ui/Amount/Amount';
import { BtnExpand } from '../../ui/BtnExpand/BtnExpand';
import { BtnMenu } from '../../ui/BtnMenu/BtnMenu';
import { ChipReimbursement } from '../../ui/ChipReimbursement/ChipReimbursement';
import { IconCircle } from '../../ui/IconCircle/IconCircle';
import { MenuItemProps } from '../../ui/Menu/Menu';
import { RowKeyValue } from '../../ui/RowKeyValue/RowKeyValue';
import { TabProps, Tabs } from '../../ui/Tabs/Tabs';
import { Time } from '../../ui/Time/Time';
import { TypographyCaption } from '../../ui/TypographyCaption/TypographyCaption';
import { UserAvatarModified } from '../../ui/UserAvatarModified/UserAvatarModified';

// links
import { LinkAccount } from '../../links/LinkAccount/LinkAccount';

// tables
import { TableBasicPayments } from '../../tables/basic/TableBasicPayments/TableBasicPayments';

// dialogs
import { DialogEditTransaction } from '../../dialogs/DialogEditTransaction/DialogEditTransaction';
import { DialogUpdateTransactionTransactions } from '../../dialogs/DialogUpdateTransactionTransactions/DialogUpdateTransactionTransactions';

// icons
import {
	ModeEditOutlined as IconModeEdit,
	ReceiptLongOutlined as IconReceiptLong,
} from '@mui/icons-material';

// theme
import { theme } from '../../../theme';

// props
export interface CardReimbursementProps {
	refetchTransactions?: () => void;
	transaction: Transaction;
}

export const CardReimbursement = ({ refetchTransactions, transaction }: CardReimbursementProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const [searchParams, setSearchParams] = useSearchParams();
	const { t } = useTranslation();

	// params
	const paramDialog = searchParams.get('dialog');
	const paramDialogTransaction = searchParams.get('dialogTransaction');

	// state
	const [isExpanded, setIsExpanded] = useState(false);
	const [tabValue, setTabValue] = useState('financing');
	const [transactions, setTransactions] = useState<Transaction[]>([]);

	// handlers
	const handleToggleCard = () => {
		setIsExpanded(!isExpanded);
	};

	const handleChangeTab = (e: SyntheticEvent, value: string) => {
		setTabValue(value);
	};

	const handleCloseDialog = () => {
		// clear all params
		const searchParams = new URLSearchParams();
		setSearchParams(searchParams);
	};

	// calculate amounts
	const payments: Payment[] = Array.isArray(transaction.payments)
		? transaction.payments.filter((p) => typeof p === 'object')
		: [];

	const amountTotal = payments.reduce(
		(sum, payment) => sum + Number(payment.amount || 0) + Number(payment.tax || 0),
		0
	);

	// get reimbursement currency
	const currencyForReimbursement = payments.length > 0 ? payments[0].currency : 'USD';

	// calculate applied amount from related transactions
	let amountApplied = 0;
	if (transactions.length) {
		transactions.forEach((t) => {
			if (typeof t === 'object') {
				const tPayments = Array.isArray(t.payments)
					? t.payments.filter((p) => typeof p === 'object')
					: [];
				tPayments.forEach((payment) => {
					if (payment.reimbursed) {
						if (payment.currency === currencyForReimbursement) {
							// Same currency - use payment amount directly
							amountApplied += Number(payment.reimbursed.amount || 0);
						} else {
							// Different currency - find reimbursement conversion
							const conversion = Array.isArray(payment.reimbursed.conversions)
								? payment.reimbursed.conversions.find(
										(c) => c.currency === currencyForReimbursement
									)
								: undefined;

							if (conversion) {
								amountApplied += Number(conversion.amount || 0);
							}
						}
					}
				});
			}
		});
	}

	const transactionToAccount = transaction.toAccount as Account;

	// define applied payments
	const paymentsApplied = [];
	for (let i = 0; i < transactions.length; i++) {
		for (let j = 0; j < transactions[i].payments.length; j++) {
			const p = transactions[i].payments[j];
			// if payment reimbursement
			if (p.reimbursed?.amount) {
				paymentsApplied.push({
					...p,
					transaction: transactions[i],
				});
			}
		}
	}

	// tabs
	const tabs: TabProps[] = [
		{
			label: t('financing'),
			value: 'financing',
		},
		{
			label: t('applied'),
			value: 'applied',
		},
	];

	// menu items
	const btnMenuItems: MenuItemProps[] = [
		{
			children: t('applyTransactions'),
			icon: <IconReceiptLong />,
			onClick: () => {
				searchParams.set('dialog', 'updateTransactionTransactions');
				searchParams.set('dialogTransaction', String(transaction.id));
				setSearchParams(searchParams);
			},
		},
		{
			children: t('editReimbursement'),
			icon: <IconModeEdit />,
			onClick: () => {
				searchParams.set('dialog', 'editTransaction');
				searchParams.set('dialogTransaction', String(transaction.id));
				setSearchParams(searchParams);
			},
		},
	];

	// fetch related transactions
	const { isLoading: isLoadingTransactions } = useFetch({
		isEnabled: Boolean(transaction?.transactions?.length),
		url: `${process.env.REACT_APP_API_URL}/transactions?id=${transaction.transactions?.join(',')}&populate=expenseType,payments,payments.conversions,payments.loan,payments.reimbursed.conversions`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setTransactions(res.data);
			}
		},
	});

	return (
		<>
			<Paper
				sx={(theme) => {
					const borderColor = isExpanded ? theme.palette.brand.blue[500] : 'transparent';
					return {
						border: `1px ${borderColor} solid`,
						position: 'relative',
					};
				}}>
				<Box sx={{ padding: theme.spacing(1.5) }}>
					<Grid container alignItems={'center'} spacing={{ xs: 1, lg: 2 }}>
						<Grid item xs={12} lg={3}>
							<Box
								sx={{
									margin: theme.spacing(0, 0, 2, 0),
									[theme.breakpoints.up('lg')]: {
										margin: 0,
									},
								}}>
								<Grid alignItems="center" container spacing={2} wrap="nowrap">
									<Grid item>
										<IconCircle
											TooltipProps={{
												title: t('reimbursement'),
											}}
											type="reimbursement"
										/>
									</Grid>
									<Grid item>
										<Grid container direction="column">
											<Grid item>
												<TypographyCaption id={transaction.id} />
											</Grid>
											<Grid item>
												<Time date={transaction.date} />
											</Grid>
										</Grid>
									</Grid>
								</Grid>
							</Box>
						</Grid>
						<Grid item xs={12} lg={2}>
							<RowKeyValue
								label={t('account')}
								value={<LinkAccount account={transactionToAccount} hasEllipsis={true} />}
							/>
						</Grid>
						<Grid item xs={12} lg={1.5}>
							<RowKeyValue
								label={t('amount')}
								value={
									<Grid
										alignItems="center"
										container
										justifyContent={{ xs: 'flex-end', lg: 'flex-start' }}
										spacing={1}>
										<Grid item xs="auto">
											<Amount
												conversions={transaction.conversions}
												currency={payments[0]?.currency}
												value={amountTotal}
											/>
										</Grid>
									</Grid>
								}
							/>
						</Grid>
						<Grid item xs={12} lg={1.5}>
							<RowKeyValue
								label={t('applied')}
								value={
									<ChipReimbursement
										amountApplied={amountApplied}
										amountTotal={amountTotal}
										currency={currencyForReimbursement}
									/>
								}
							/>
						</Grid>
						<Grid item xs={12} lg={2.5}>
							<RowKeyValue
								label={t('updated')}
								value={
									<UserAvatarModified
										activityTypes={['TRANSACTION_UPDATED', 'TRANSACTION_CREATED']}
										date={transaction.modified || transaction.created}
										transaction={transaction}
									/>
								}
							/>
						</Grid>
						<Grid item xs={12} lg={true}>
							<Grid alignItems="center" container justifyContent={{ lg: 'flex-end' }} spacing={1}>
								{btnMenuItems.length > 0 && (
									<Grid
										item
										sx={{
											position: 'absolute',
											top: 0,
											right: theme.spacing(1),
											[theme.breakpoints.up('lg')]: {
												position: 'relative',
												top: 'auto',
												right: 'auto',
											},
										}}>
										<BtnMenu items={btnMenuItems} />
									</Grid>
								)}
								<Grid item>
									<BtnExpand
										isExpanded={isExpanded}
										label={isExpanded ? t('hideDetails') : t('seeDetails')}
										onClick={handleToggleCard}
									/>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</Box>
				{isExpanded && (
					<TabContext value={tabValue}>
						<Tabs
							BoxProps={{
								sx: {
									marginTop: {
										lg: theme.spacing(3),
									},
								},
							}}
							hasBorder={false}
							onChange={handleChangeTab}
							tabs={tabs}
							value={tabValue}
						/>
						<TabPanel value="applied">
							<TableBasicPayments
								data={paymentsApplied}
								headersIncluded={[
									'hash',
									'recorded',
									'reimbursed',
									'source',
									'total',
									'transaction',
								]}
								isLoading={isLoadingTransactions}
								transaction={transaction}
							/>
						</TabPanel>
						<TabPanel value="financing">
							<TableBasicPayments
								data={payments}
								headersIncluded={['hash', 'recorded', 'source', 'status', 'total']}
								transaction={transaction}
							/>
						</TabPanel>
					</TabContext>
				)}
			</Paper>
			<DialogEditTransaction
				isOpen={
					paramDialog === 'editTransaction' && paramDialogTransaction === String(transaction.id)
				}
				onClose={handleCloseDialog}
				onSuccess={() => {
					if (refetchTransactions) {
						refetchTransactions();
					}
				}}
				transaction={transaction}
			/>
			<DialogUpdateTransactionTransactions
				isOpen={
					paramDialog === 'updateTransactionTransactions' &&
					paramDialogTransaction === String(transaction.id)
				}
				onClose={handleCloseDialog}
				onSuccess={() => {
					if (refetchTransactions) {
						refetchTransactions();
					}
				}}
				transaction={transaction}
			/>
		</>
	);
};
